import React, { useState, useEffect } from "react";
import { BarChart, LineChart } from "@mui/x-charts";
import { useTranslation } from "react-i18next";

export default function OrderChartsUi({ value, onFilterChange }) {
  const { t } = useTranslation();

  // --- State for Filters ---
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Generate Array of Years
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  // Array of Months
  const months = [
    { value: "1", label: t("January") || "January" },
    { value: "2", label: t("February") || "February" },
    { value: "3", label: t("March") || "March" },
    { value: "4", label: t("April") || "April" },
    { value: "5", label: t("May") || "May" },
    { value: "6", label: t("June") || "June" },
    { value: "7", label: t("July") || "July" },
    { value: "8", label: t("August") || "August" },
    { value: "9", label: t("September") || "September" },
    { value: "10", label: t("October") || "October" },
    { value: "11", label: t("November") || "November" },
    { value: "12", label: t("December") || "December" },
  ];

  // --- Handle Filter Changes ---
  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
    setSelectedMonth(null);
    if (onFilterChange) onFilterChange({ year: newYear, month: null });
  };

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setSelectedMonth(newMonth);
    setSelectedYear(null);
    if (onFilterChange) onFilterChange({ year: null, month: newMonth });
  };

  // --- Process Chart Data ---
  const revenueData = value?.revenue_by_time || [];
  const revenueLabels = revenueData.map((item) => item.time);
  const revenueValues = revenueData.map((item) => parseFloat(item.revenue));

  const paymentData = value?.order_by_payment_method || [];
  const paymentLabels = paymentData.map((item) => item.name || t("unknown") || "Unknown");
  const paymentValues = paymentData.map((item) => item.value);

  // --- 🎨 CUSTOM STYLES FOR STABLE CLASSES ---
  // We use the sx prop to target specific internal MUI classes.
  // These class names (.MuiChartsAxis-...) will NOT change on the server.
  const chartSettingStyles = {
    // 1. The Axis Lines (vertical and horizontal main lines)
    "& .MuiChartsAxis-line": {
      stroke: "#ffffff !important", // Force white color
      strokeWidth: 2,
    },
    // 2. The Ticks (small lines pointing to numbers)
    "& .MuiChartsAxis-tick": {
      stroke: "#ffffff !important",
    },
    // 3. The Tick Labels (The numbers/text on the axis)
    "& .MuiChartsAxis-tickLabel": {
      fill: "#ffffff !important", // 'fill' is used for SVG text, not 'color'
      fontWeight: "500",
    },
    // 4. The Grid Lines (background grid)
    "& .MuiChartsGrid-line": {
      stroke: "rgba(255, 255, 255, 0.2) !important", // Subtle white grid
    },
  };

  return (
    <>
      {/* --- REVENUE LINE CHART --- */}
      <div className="chart-wrap w-full bg-thirdly flex flex-col justify-center gap-3 px-4 pt-6 pb-3 rounded-lg">
        <div className="flex sm:flex-row flex-col justify-between items-center mb-2">
           <div className="flex gap-3">
            <select
              value={selectedYear || ""}
              onChange={handleYearChange}
              className="bg-white/10 border border-white/20 text-white text-sm rounded-md px-3 py-1 outline-none focus:border-blue-500 cursor-pointer"
            >
            <option value="" disabled className='text-gray'>Select Year</option>
              {years.map((year) => (
                <option key={year} value={year} className="text-black">
                  {year}
                </option>
              ))}
            </select>

            <select
              value={selectedMonth || ""}
              onChange={handleMonthChange}
              className="bg-white/10 border border-white/20 text-white text-sm rounded-md px-3 py-1 outline-none focus:border-blue-500 cursor-pointer"
            >
            <option value="" disabled className='text-gray'>Select Month</option>
              {months.map((m) => (
                <option key={m.value} value={m.value} className="text-black">
                  {m.label}
                </option>
              ))}
            </select>
          </div>

           <label className="text-white font-semibold text-lg text-right sm:mt-0 mt-3">
            {t("charts.revenue_chart")}
          </label>
        </div>

        <LineChart
          xAxis={[{ data: revenueLabels, scaleType: "point" }]}
          series={[
            {
              data: revenueValues,
              color: "#3b82f6",
              area: true,
              curve: "natural",
            },
          ]}
          height={370}
          grid={{ vertical: true, horizontal: true }}
          // Apply the styles here
          sx={chartSettingStyles}
        />
      </div>

      {/* --- PAYMENT METHOD BAR CHART --- */}
      <div className="chart-wrap w-full bg-thirdly flex flex-col justify-center my-7 gap-3 px-1 pt-6 pb-3 rounded-lg">
        <label className="text-white mb-3 font-semibold text-lg tracking-wide text-right mr-4">
          {t("charts.ad_distribution_by_payment_methods")}
        </label>
        <BarChart
          xAxis={[{ id: "barCategories", data: paymentLabels, scaleType: "band" }]}
          series={[{ data: paymentValues, color: "#479DFF" }]}
          height={370}
          // Apply the styles here
          sx={chartSettingStyles}
        />
      </div>
    </>
  );
}